import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWikiV2Client, WIKI_QUERY_DEFAULTS } from '../../../hooks/useWikiV2';
import { findWikiCategory, indexWikiCategories, wikiKeys } from './wikiData';

export { findWikiCategory, indexWikiCategories, wikiKeys };

/**
 * React Query helpers for the Wiki, on top of `useWikiV2Client()`. Query keys
 * are `wikiKeys` (see ./wikiData.js) — never hand-build a Wiki key.
 */

/**
 * Generic Wiki read. `path` is relative to /api (e.g. '/wiki/home'); a falsy
 * path disables the query. `opts` are passed to `useQuery` (select, enabled,
 * staleTime, refetchInterval, …) over the Wiki defaults (2 min stale, 1 retry).
 */
export function useWikiQuery(key, path, opts = {}) {
    const client = useWikiV2Client();
    const { enabled = true, ...rest } = opts;
    return useQuery({
        queryKey: key,
        queryFn: () => client.get(path),
        ...WIKI_QUERY_DEFAULTS,
        ...rest,
        enabled: Boolean(path) && enabled,
    });
}

/**
 * Generic Wiki write. `mutationFn(client, variables)`; on success it
 * invalidates `invalidate` (scopes as for `invalidateWiki`) then calls
 * `onSuccess`. Other `useMutation` options pass through.
 */
export function useWikiMutation(mutationFn, { invalidate = [], onSuccess, ...rest } = {}) {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (variables) => mutationFn(client, variables),
        ...rest,
        onSuccess: async (data, variables, context) => {
            if (invalidate.length) await invalidateWiki(queryClient, ...invalidate);
            if (onSuccess) await onSuccess(data, variables, context);
        },
    });
}

/**
 * Invalidate Wiki queries by scope. A string scope is a key prefix under
 * 'wiki' ('categories', 'home', 'counts', 'pages', 'page', 'ack', 'ask',
 * 'brain'); an array scope is the rest of an exact key prefix (['page', id]).
 * No scopes invalidates every Wiki query. After an acknowledgement, invalidate
 * 'ack', 'home', 'counts' and ['page', id] so the nav badge, Home panel and To
 * acknowledge view agree.
 */
export function invalidateWiki(queryClient, ...scopes) {
    if (!queryClient) return Promise.resolve();
    if (scopes.length === 0) return queryClient.invalidateQueries({ queryKey: wikiKeys.all() });
    return Promise.all(scopes.filter(Boolean).map((scope) => queryClient.invalidateQueries({
        queryKey: ['wiki', ...(Array.isArray(scope) ? scope : [scope])],
    })));
}

/**
 * `GET /wiki/categories` → `{ ...query, tree, flat, byId, find(id) }`.
 * The raw response stays in the cache under ['wiki','categories'].
 */
export function useWikiCategories(opts = {}) {
    const query = useWikiQuery(wikiKeys.categories(), '/wiki/categories', opts);
    const index = useMemo(() => indexWikiCategories(query.data), [query.data]);
    return useMemo(() => ({
        ...query,
        tree: index.tree,
        flat: index.flat,
        byId: index.byId,
        find: (id) => findWikiCategory(index, id),
    }), [query, index]);
}

/**
 * `GET /wiki/categories/{id}` under ['wiki','category',id] — one category with
 * its `access_summary` (who can actually view and edit it, #871). The list
 * cache stays the source for everything else on the category view.
 */
export function useWikiCategory(id, opts = {}) {
    return useWikiQuery(wikiKeys.category(id), id ? `/wiki/categories/${encodeURIComponent(id)}` : null, opts);
}

/** `GET /wiki/home` under ['wiki','home']. */
export function useWikiHome(opts = {}) {
    return useWikiQuery(wikiKeys.home(), '/wiki/home', opts);
}

/** `GET /wiki/counts` under ['wiki','counts'] — `{acknowledge, brain_pending}` for the nav. */
export function useWikiCounts(opts = {}) {
    return useWikiQuery(wikiKeys.counts(), '/wiki/counts', opts);
}

/** `GET /wiki/pages/{id}` under ['wiki','page',id]. */
export function useWikiPage(id, opts = {}) {
    return useWikiQuery(wikiKeys.page(id), id ? `/wiki/pages/${encodeURIComponent(id)}` : null, opts);
}
